import {
  AUTOMATION_MARKETPLACE_URL,
  automationListingInstallUrl,
  automationListingUrl,
} from "../constants/automation-api-urls";
import type {
  IInstallMarketplaceAutomationResult,
  IMarketplaceAutomation,
  IPublishAutomationProps,
  IUnpublishAutomationProps,
} from "../types";
import type { ApiClient } from "./client";

// NOTE: NSAI-5237 / NSAI-5272 — organization-wide automation marketplace.
// Browsing and installing are gated server-side by marketplace_automation:read
// (install additionally needs automation:write, since it creates an automation
// in the caller's project); publishing by marketplace_automation:write. All
// routes are scoped to the current project via the x-project-uuid header the
// ApiClient already sends.
export class AutomationMarketplaceApi {
  constructor(private readonly apiClient: ApiClient) {}

  async getMarketplaceAutomations(): Promise<IMarketplaceAutomation[]> {
    const response = await this.apiClient.get<IMarketplaceAutomation[]>(
      AUTOMATION_MARKETPLACE_URL,
    );
    return response.data;
  }

  async publish({
    automationType,
    automationUuid,
    data,
  }: IPublishAutomationProps): Promise<IMarketplaceAutomation> {
    const response = await this.apiClient.post<IMarketplaceAutomation>(
      automationListingUrl({ automationType, automationUuid }),
      data,
    );
    return response.data;
  }

  async updateListing({
    automationType,
    automationUuid,
    data,
  }: IPublishAutomationProps): Promise<IMarketplaceAutomation> {
    const response = await this.apiClient.put<IMarketplaceAutomation>(
      automationListingUrl({ automationType, automationUuid }),
      data,
    );
    return response.data;
  }

  async unpublish(props: IUnpublishAutomationProps): Promise<void> {
    await this.apiClient.delete(automationListingUrl(props));
  }

  // NOTE: Creates an independent copy of the listed automation in the caller's
  // project — a scheduled task arrives paused, a webhook with a fresh token.
  async install(
    listingUuid: string,
  ): Promise<IInstallMarketplaceAutomationResult> {
    const response =
      await this.apiClient.post<IInstallMarketplaceAutomationResult>(
        automationListingInstallUrl(listingUuid),
      );
    return response.data;
  }
}
